import { useEffect, useState } from 'react';

/**
 * Reactive matchMedia read, resolved synchronously so the first paint matches the real viewport.
 *
 * Chakra's `useBreakpointValue`/`useMediaQuery` resolve in an effect: they report the `base` entry
 * on first paint and then snap, so every caller pays a second render on mount. That is one wasted
 * render when a page calls it once, and a second render of every row when a list calls it per item.
 */
export function useMediaQueryMatch(query: string): boolean {
	const [matches, setMatches] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
	useEffect(() => {
		const mql = window.matchMedia(query);
		setMatches(mql.matches);
		const handler = (event: MediaQueryListEvent) => setMatches(event.matches);
		mql.addEventListener('change', handler);
		return () => mql.removeEventListener('change', handler);
	}, [query]);
	return matches;
}
